<template>
  <v-dialog :value="!!file" max-width="500" persistent @input="v => !v && $emit('cancel')">
    <v-card class="crop-card">
      <div class="crop-header">
        <span class="crop-title">ปรับตำแหน่งรูปโปรไฟล์</span>
        <v-btn icon small @click="$emit('cancel')"><v-icon>mdi-close</v-icon></v-btn>
      </div>

      <div class="crop-hint">ลากเพื่อปรับตำแหน่ง • เลื่อนล้อ/หยิกนิ้วเพื่อซูม</div>

      <div class="crop-stage">
        <div class="crop-circle-wrap">
          <div ref="circle" class="crop-circle"
               @mousedown.prevent="onDown"
               @touchstart.prevent="onDown"
               @wheel.prevent="onWheel">
            <img v-if="src" ref="img" :src="src" class="crop-img" :style="imgStyle"
                 draggable="false" @load="onLoad" />
          </div>
          <div class="crop-overlay" />
        </div>
      </div>

      <div class="crop-zoom-row">
        <v-icon size="18" color="#9ca3af">mdi-magnify-minus-outline</v-icon>
        <input type="range" v-model.number="scale"
               :min="minScale" :max="minScale * 4" step="0.005"
               class="crop-slider" />
        <v-icon size="18" color="#9ca3af">mdi-magnify-plus-outline</v-icon>
      </div>

      <div class="crop-actions">
        <v-btn outlined height="40" @click="$emit('cancel')">ยกเลิก</v-btn>
        <v-btn class="crop-btn-ok" height="40" :loading="saving" @click="confirm">ยืนยัน</v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'AvatarCropperDialog',
  props: {
    file: { type: File, default: null },
  },

  data() {
    return {
      src: '',
      tx: 0, ty: 0,
      scale: 1,
      minScale: 1,
      baseW: 0, baseH: 0,
      drag: null,
      saving: false,
    };
  },

  computed: {
    imgStyle() {
      if (!this.baseW) return { opacity: 0 };
      return {
        width: this.baseW + 'px',
        height: this.baseH + 'px',
        left: `calc(50% - ${this.baseW / 2}px)`,
        top: `calc(50% - ${this.baseH / 2}px)`,
        transform: `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`,
      };
    },
  },

  watch: {
    file(f) {
      if (f) {
        if (this.src) URL.revokeObjectURL(this.src);
        this.src = URL.createObjectURL(f);
        this.tx = 0; this.ty = 0;
        this.scale = 1; this.minScale = 1;
        this.baseW = 0; this.baseH = 0;
      } else {
        this.src = '';
      }
    },
    scale() {
      this.clamp();
    },
  },

  methods: {
    onLoad() {
      this.$nextTick(() => {
        const img = this.$refs.img;
        const circle = this.$refs.circle;
        if (!img || !circle) return;
        const D = circle.offsetWidth;
        const nw = img.naturalWidth;
        const nh = img.naturalHeight;
        const s = Math.max(D / nw, D / nh);
        this.baseW = nw * s;
        this.baseH = nh * s;
        this.minScale = 1;
        this.scale = 1;
        this.tx = 0; this.ty = 0;
      });
    },

    clamp() {
      const D = this.$refs.circle?.offsetWidth || 300;
      const maxX = Math.max(0, (this.baseW * this.scale - D) / 2);
      const maxY = Math.max(0, (this.baseH * this.scale - D) / 2);
      this.tx = Math.max(-maxX, Math.min(maxX, this.tx));
      this.ty = Math.max(-maxY, Math.min(maxY, this.ty));
    },

    onDown(e) {
      const isTouch = !!e.touches;
      const t0 = isTouch ? e.touches[0] : e;
      this.drag = {
        startX: t0.clientX - this.tx,
        startY: t0.clientY - this.ty,
        pinchDist: isTouch && e.touches.length === 2 ? this.pinchDist(e.touches) : null,
        pinchScale: this.scale,
      };

      const move = (me) => {
        if (!this.drag) return;
        const isTouchMove = !!me.touches;

        if (isTouchMove && me.touches.length === 2 && this.drag.pinchDist) {
          const d = this.pinchDist(me.touches);
          this.scale = Math.max(this.minScale, this.drag.pinchScale * d / this.drag.pinchDist);
          return;
        }
        const t = isTouchMove ? me.touches[0] : me;
        this.tx = t.clientX - this.drag.startX;
        this.ty = t.clientY - this.drag.startY;
        this.clamp();
      };

      const up = () => {
        this.drag = null;
        document.removeEventListener('mousemove', move);
        document.removeEventListener('mouseup', up);
        document.removeEventListener('touchmove', move);
        document.removeEventListener('touchend', up);
      };

      document.addEventListener('mousemove', move);
      document.addEventListener('mouseup', up);
      document.addEventListener('touchmove', move, { passive: true });
      document.addEventListener('touchend', up);
    },

    pinchDist(touches) {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    },

    onWheel(e) {
      const delta = e.deltaY > 0 ? -0.08 : 0.08;
      this.scale = Math.max(this.minScale, Math.min(this.minScale * 4, this.scale + delta));
    },

    confirm() {
      this.saving = true;
      try {
        const canvas = document.createElement('canvas');
        const OUTPUT = 600;
        canvas.width = OUTPUT;
        canvas.height = OUTPUT;
        const ctx = canvas.getContext('2d');

        const circle = this.$refs.circle;
        const img = this.$refs.img;
        const cRect = circle.getBoundingClientRect();
        const iRect = img.getBoundingClientRect();

        const ratio = OUTPUT / cRect.width;
        const dx = (iRect.left - cRect.left) * ratio;
        const dy = (iRect.top - cRect.top) * ratio;
        const dw = iRect.width * ratio;
        const dh = iRect.height * ratio;

        ctx.drawImage(img, dx, dy, dw, dh);

        canvas.toBlob(blob => {
          const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
          this.$emit('done', file);
          this.saving = false;
        }, 'image/jpeg', 0.92);
      } catch {
        this.saving = false;
      }
    },
  },

  beforeDestroy() {
    if (this.src) URL.revokeObjectURL(this.src);
  },
};
</script>

<style scoped>
.crop-card { border-radius: 18px !important; overflow: hidden; }

.crop-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 6px;
}
.crop-title {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
}
.crop-hint {
  text-align: center;
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 12px;
}

.crop-stage {
  display: flex;
  justify-content: center;
  padding: 0 20px;
  overflow: hidden;
}

.crop-circle-wrap {
  position: relative;
  width: min(400px, 80vw);
  height: min(400px, 80vw);
  flex-shrink: 0;
}

.crop-circle {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  cursor: grab;
  background: #e2e8f0;
  position: relative;
  user-select: none;
  touch-action: none;
}
.crop-circle:active { cursor: grabbing; }

.crop-img {
  position: absolute;
  transform-origin: center;
  user-select: none;
  pointer-events: none;
}

/* dark overlay with circular hole exactly over the circle */
.crop-overlay {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.48);
  pointer-events: none;
  z-index: 10;
}

.crop-zoom-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 28px 4px;
}
.crop-slider {
  flex: 1;
  height: 4px;
  accent-color: #2563eb;
  cursor: pointer;
}

.crop-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px 20px;
}
.crop-btn-ok {
  background: #2563eb !important;
  color: white !important;
  text-transform: none;
  border-radius: 8px !important;
}
</style>
